import React, { useEffect, useMemo } from "react";

import {
  filterPillarTableNestedTables,
  hasPillarTableNestedRows,
} from "shared/components/pillar-table/nested/pillarTableNestedTableHelpers";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";

/**
 * Every nested table a row declares, rendered as its own full-width row
 * underneath it.
 *
 * A row can declare any number of them — a workflow instance might have
 * certifications, invoice items and conferences all at once. Each
 * `PillarTable.NestedTable` knows only its own relation, so this is where they
 * are collected and where the row's expanded state is kept honest: a row is
 * only togglable while at least one of its relations has rows.
 *
 * Renders nothing when a row has no nested tables at all, but still mounts, so
 * that the toggle is retracted for rows whose relations came back empty.
 */
const PillarTableNestedTableComponentWrapper = () => {
  const { columns } = usePillarTableContext();
  const { rowValue, setRowExpanded } = usePillarTableRowContext();

  const nestedTables = useMemo(
    () => filterPillarTableNestedTables(columns),
    [columns],
  );
  const hasNestedRows = hasPillarTableNestedRows(rowValue, nestedTables);

  // "none" means there is nothing to expand, so no arrow is drawn.
  useEffect(() => {
    setRowExpanded((previous) => {
      if (!hasNestedRows) {
        return "none";
      }
      return previous === "none" ? "close" : previous;
    });
  }, [hasNestedRows, setRowExpanded]);

  return <>{nestedTables}</>;
};

export default PillarTableNestedTableComponentWrapper;
